import React, { useState, useEffect, useRef } from 'react'


const UOM_SUGGESTIONS = [
  'EA', 'EACH', 'LF', 'SF', 'SY', 'CF', 'CY', 'TON', 'LB',
  'GAL', 'QT', 'OZ', 'BAG', 'BOX', 'CASE', 'PALLET', 'ROLL',
  'BUNDLE', 'SHEET', 'LOT', 'HR', 'DAY'
]

const CATEGORY_SUGGESTIONS = [
  'Irrigation', 'Planting', 'Aggregate', 'Lighting',
  'Hardscape', 'Herbicide', 'Fertilizer', 'General'
]

const normalizeUom = (value) => {
  const upper = String(value || '').trim().toUpperCase()
  if (!upper) return ''
  if (upper === 'S.F.' || upper === 'SF') return 'SF'
  if (upper === 'L.F.' || upper === 'LF') return 'LF'
  if (upper === 'GAL.' || upper === 'GALLON' || upper === 'GAL') return 'GAL'
  if (upper === 'C.Y.' || upper === 'CY') return 'CY'
  if (upper === 'EACH') return 'EA'
  const match = UOM_SUGGESTIONS.find(u => u === upper)
  return match || upper
}

const parseNumeric = (text) => {
  const cleaned = String(text || '').replace(/[$,\s]/g, '')
  const match = cleaned.match(/[\d]+(?:\.\d+)?/)
  return match ? parseFloat(match[0]) : null
}

const TOKEN_RULES = [
  { field: 'upc', label: 'UPC', re: /UPC\s*#?\s*:?\s*(\d[\d\s-]{9,15}\d)/ig, clean: v => v.replace(/[\s-]/g, '') },
  { field: 'manufacturerSku', label: 'MFR #', re: /(?:MFR|MFG|MPN)\s*(?:P\/?N|#|PART)?\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig },
  { field: 'vendorSku', label: 'P/N', re: /\bP\/?N\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig },
  { field: 'manufacturerSku', label: 'S/N', re: /\bS\/?N\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig },
  { field: 'vendorSku', label: 'SKU', re: /\b(?:SKU|ITEM\s*#|CAT(?:ALOG)?\s*#)\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig }
]

function detectLineTokens (rawText) {
  if (!rawText) return []
  const out = []
  const seen = new Set()
  for (const rule of TOKEN_RULES) {
    rule.re.lastIndex = 0
    let m
    while ((m = rule.re.exec(rawText)) !== null) {
      let value = (m[1] || '').trim()
      if (rule.clean) value = rule.clean(value)
      if (rule.field === 'upc' && !(value.length >= 11 && value.length <= 13)) continue
      const key = `${rule.field}|${value}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push({ field: rule.field, label: rule.label, value })
    }
  }
  return out
}

const inferQuoteFields = (text) => {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim()
  if (!normalized) return { description: '', vendorSku: '', category: '', quantity: '', unitPrice: '', uom: '' }

  let remaining = normalized

  const tokens = detectLineTokens(normalized)
  const skuToken = tokens.find(t => t.field === 'vendorSku') || tokens.find(t => t.field === 'manufacturerSku')
  const vendorSku = skuToken ? skuToken.value : ''

  const priceMatches = Array.from(normalized.matchAll(/\$[\d,]+(?:\.\d{1,2})?/g))
  const unitPrice = priceMatches.length > 0 ? parseNumeric(priceMatches[priceMatches.length - 1][0]) : ''

  const qtyUomMatch = normalized.match(/(\d[\d,]*(?:\.\d+)?)\s*(EA|EACH|LF|SF|SY|CF|CY|TON|LB|GAL|QT|BAG|BOX|CASE|ROLL|BUNDLE|LOT)\b/i)
  const quantity = qtyUomMatch ? parseNumeric(qtyUomMatch[1]) : ''
  const uom = qtyUomMatch ? normalizeUom(qtyUomMatch[2]) : ''

  for (const token of tokens) {
    remaining = remaining.replace(new RegExp(token.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), ' ')
  }
  for (const match of priceMatches) {
    remaining = remaining.replace(match[0], ' ')
  }
  if (qtyUomMatch) {
    remaining = remaining.replace(qtyUomMatch[0], ' ')
  }
  remaining = remaining.replace(/\b(?:SKU|ITEM|CAT(?:ALOG)?|MFR|MFG|MPN|UPC|P\/?N|S\/?N)\s*#?\s*:?\s*/gi, ' ')
  const description = remaining.replace(/\s+/g, ' ').trim()

  return { description, vendorSku, category: '', quantity, unitPrice, uom }
}


let workshopDatalistSeq = 0

export default function QuoteTextWorkshop ({ lineItem, lineIndex, parserRawText, editable, onApply, onNewItem, onNavigate }) {
  const sourceRef = useRef(null)
  const [selectedText, setSelectedText] = useState('')
  const [draft, setDraft] = useState({ description: '', vendorSku: '', category: '', quantity: '', unitPrice: '', uom: '' })
  const [scratchpad, setScratchpad] = useState('')
  const [scratchExpanded, setScratchExpanded] = useState(false)
  const [isNewItem, setIsNewItem] = useState(false)
  const uomListId = useRef(`ws-uom-${++workshopDatalistSeq}`).current
  const catListId = useRef(`ws-cat-${++workshopDatalistSeq}`).current

  const sourceText = (lineItem && lineItem.rawText) || parserRawText || ''

  useEffect(() => {
    if (!lineItem) return
    setDraft({
      description: lineItem.description || '',
      vendorSku: lineItem.vendorSku || '',
      category: lineItem.category || '',
      quantity: lineItem.quantity ?? '',
      unitPrice: lineItem.unitPrice ?? '',
      uom: lineItem.uom || ''
    })
    setSelectedText('')
    setIsNewItem(false)
  }, [lineItem?._id, lineIndex])

  const captureSelection = () => {
    const selection = window.getSelection && window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const text = String(selection.toString() || '').replace(/\s+/g, ' ').trim()
    if (!text) return
    const node = selection.anchorNode
    if (sourceRef.current && node && sourceRef.current.contains(node)) {
      setSelectedText(text)
    }
  }

  const assignSelection = (field) => {
    if (!selectedText) return
    if (field === 'quantity' || field === 'unitPrice') {
      const num = parseNumeric(selectedText)
      if (num !== null) setDraft(prev => ({ ...prev, [field]: num }))
    } else if (field === 'uom') {
      setDraft(prev => ({ ...prev, uom: normalizeUom(selectedText) }))
    } else {
      setDraft(prev => ({ ...prev, [field]: selectedText }))
    }
  }

  const appendToScratchpad = () => {
    if (!selectedText) return
    setScratchpad(prev => [prev, selectedText].map(v => String(v || '').trim()).filter(Boolean).join('\n'))
    if (!scratchExpanded) setScratchExpanded(true)
  }

  const startNewItem = () => {
    if (!selectedText) return
    const inferred = inferQuoteFields(selectedText)
    setDraft(inferred)
    setIsNewItem(true)
  }

  const resetDraft = () => {
    if (!lineItem) return
    setDraft({
      description: lineItem.description || '',
      vendorSku: lineItem.vendorSku || '',
      category: lineItem.category || '',
      quantity: lineItem.quantity ?? '',
      unitPrice: lineItem.unitPrice ?? '',
      uom: lineItem.uom || ''
    })
    setSelectedText('')
    setIsNewItem(false)
    const sel = window.getSelection && window.getSelection()
    if (sel && sel.removeAllRanges) sel.removeAllRanges()
  }

  const handleApply = () => {
    if (!editable) return
    const updates = {
      description: String(draft.description || '').trim(),
      vendorSku: String(draft.vendorSku || '').trim(),
      category: String(draft.category || '').trim(),
      quantity: draft.quantity === '' || draft.quantity == null ? 0 : Number(draft.quantity),
      unitPrice: draft.unitPrice === '' || draft.unitPrice == null ? 0 : Number(draft.unitPrice),
      uom: String(draft.uom || '').trim().toUpperCase()
    }
    if (isNewItem) {
      onNewItem(updates)
      setIsNewItem(false)
    } else {
      onApply(lineIndex, updates)
    }
  }

  const draftExtension = (Number(draft.quantity) || 0) * (Number(draft.unitPrice) || 0)

  const isFieldChanged = (field) => {
    if (!lineItem || isNewItem) return false
    const original = field === 'quantity' || field === 'unitPrice'
      ? (lineItem[field] ?? 0)
      : (lineItem[field] || '')
    const current = draft[field]
    return String(current ?? '') !== String(original ?? '')
  }

  // ── Task 3: Full render ────────────────────────────────────────────────

  if (!lineItem && !isNewItem) {
    return (
      <div style={{ background: 'var(--df-ink, #1c2419)', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, padding: 24 }}>
        <span style={{ color: '#888', fontSize: 13 }}>Select a line item to start the workshop</span>
      </div>
    )
  }

  const fieldCardStyle = (field) => ({
    background: '#2a3a25',
    border: isFieldChanged(field) ? '1.5px solid #ff9800' : '1px solid #3a5a32',
    borderRadius: 4,
    padding: '5px 8px'
  })

  const fieldLabelStyle = { fontSize: 8, color: '#888', textTransform: 'uppercase', marginBottom: 2, display: 'block' }

  const fieldInputStyle = {
    width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', outline: 'none',
    fontSize: 11, color: '#fff', fontFamily: 'inherit', padding: 0
  }

  return (
    <div style={{
      background: 'var(--df-ink, #1c2419)', flex: 1, display: 'flex', flexDirection: 'column',
      gap: 10, padding: 14, borderRadius: 8, minHeight: 0, overflow: 'auto'
    }}>
      {/* Source text area */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{
            fontSize: 9, padding: '2px 8px', borderRadius: 10, background: '#2e7d32', color: '#fff',
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em'
          }}>
            {isNewItem ? 'New Item' : `Raw Text — Line ${lineIndex + 1}`}
          </span>
          <span style={{ fontSize: 10, color: '#888' }}>Highlight text, then route to a field</span>
        </div>
        <div
          ref={sourceRef}
          onMouseUp={captureSelection}
          onKeyUp={captureSelection}
          style={{
            background: '#151d12', border: '1px solid #3a5a32', borderRadius: 6,
            padding: 10, color: '#c4ccc1', fontFamily: '"Courier New", monospace',
            fontSize: 12, lineHeight: 1.8, minHeight: 50, maxHeight: 120,
            overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', userSelect: 'text'
          }}
        >
          {sourceText || 'No source text available for this line item.'}
        </div>
      </div>

      {/* Route buttons */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {selectedText && (
          <span style={{
            fontSize: 9, padding: '2px 8px', borderRadius: 10, background: '#333', color: '#aaa',
            maxWidth: 220, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'
          }}>
            Selected: &quot;{selectedText}&quot;
          </span>
        )}
        <button
          style={{ fontSize: 10, padding: '3px 10px', borderRadius: 12, background: '#2e7d32', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          disabled={!selectedText}
          onClick={startNewItem}
        >New Item</button>
        {['description', 'vendorSku', 'category', 'quantity', 'unitPrice', 'uom'].map(field => {
          const labels = { description: 'Desc', vendorSku: 'SKU', category: 'Category', quantity: 'Qty', unitPrice: 'Price', uom: 'UOM' }
          return (
            <button
              key={field}
              style={{ fontSize: 10, padding: '3px 10px', borderRadius: 12, border: '1px solid #5a7a52', background: 'transparent', color: '#c4ccc1', cursor: 'pointer' }}
              disabled={!selectedText}
              onClick={() => assignSelection(field)}
            >{labels[field]}</button>
          )
        })}
        <button
          style={{ fontSize: 10, padding: '3px 10px', borderRadius: 12, border: '1px solid #5a7a52', background: 'transparent', color: '#c4ccc1', cursor: 'pointer' }}
          disabled={!selectedText}
          onClick={appendToScratchpad}
        >Scratch</button>
        <span
          style={{ fontSize: 10, color: '#888', cursor: 'pointer', marginLeft: 4 }}
          onClick={resetDraft}
        >Clear</span>
      </div>

      {/* Scratchpad */}
      <div
        style={{ background: '#2a3a25', border: '1px solid #3a5a32', borderRadius: 4, padding: '6px 10px', fontSize: 10, color: '#888', cursor: 'pointer' }}
        onClick={() => setScratchExpanded(prev => !prev)}
      >
        Scratchpad {scratchExpanded ? '▾' : '▸'} {scratchpad && `(${scratchpad.split('\n').filter(Boolean).length} lines)`}
      </div>
      {scratchExpanded && (
        <textarea
          value={scratchpad}
          onChange={e => setScratchpad(e.target.value)}
          placeholder="Paste or route text here for reference..."
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box', background: '#2a3a25', border: '1px solid #3a5a32',
            borderRadius: 4, padding: '6px 10px', fontSize: 11, color: '#c4ccc1',
            fontFamily: '"Courier New", monospace', resize: 'vertical', outline: 'none'
          }}
        />
      )}

      {/* Working fields */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{
            fontSize: 9, padding: '2px 8px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32',
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em'
          }}>Working Fields</span>
          <span style={{ fontSize: 10, color: '#888' }}>Edit directly or route from source text</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 6 }}>
          <div style={fieldCardStyle('description')}>
            <span style={fieldLabelStyle}>Description {isFieldChanged('description') && <span style={{ color: '#ff9800' }}>edited</span>}</span>
            <input style={fieldInputStyle} value={draft.description} onChange={e => setDraft(prev => ({ ...prev, description: e.target.value }))} disabled={!editable} />
          </div>
          <div style={fieldCardStyle('vendorSku')}>
            <span style={fieldLabelStyle}>SKU {isFieldChanged('vendorSku') && <span style={{ color: '#ff9800' }}>edited</span>}</span>
            <input style={fieldInputStyle} value={draft.vendorSku} onChange={e => setDraft(prev => ({ ...prev, vendorSku: e.target.value }))} disabled={!editable} />
          </div>
          <div style={fieldCardStyle('category')}>
            <span style={fieldLabelStyle}>Category {isFieldChanged('category') && <span style={{ color: '#ff9800' }}>edited</span>}</span>
            <input style={fieldInputStyle} list={catListId} value={draft.category} onChange={e => setDraft(prev => ({ ...prev, category: e.target.value }))} disabled={!editable} />
            <datalist id={catListId}>{CATEGORY_SUGGESTIONS.map(c => <option key={c} value={c} />)}</datalist>
          </div>
          <div style={fieldCardStyle('quantity')}>
            <span style={fieldLabelStyle}>Qty {isFieldChanged('quantity') && <span style={{ color: '#ff9800' }}>edited</span>}</span>
            <input style={{ ...fieldInputStyle, textAlign: 'right' }} type="number" step="any" value={draft.quantity} onChange={e => setDraft(prev => ({ ...prev, quantity: e.target.value }))} disabled={!editable} />
          </div>
          <div style={fieldCardStyle('unitPrice')}>
            <span style={fieldLabelStyle}>Price {isFieldChanged('unitPrice') && <span style={{ color: '#ff9800' }}>edited</span>}</span>
            <input style={{ ...fieldInputStyle, textAlign: 'right' }} type="number" step="any" value={draft.unitPrice} onChange={e => setDraft(prev => ({ ...prev, unitPrice: e.target.value }))} disabled={!editable} />
          </div>
          <div style={fieldCardStyle('uom')}>
            <span style={fieldLabelStyle}>UOM {isFieldChanged('uom') && <span style={{ color: '#ff9800' }}>edited</span>}</span>
            <input style={{ ...fieldInputStyle, textTransform: 'uppercase' }} list={uomListId} value={draft.uom} onChange={e => setDraft(prev => ({ ...prev, uom: e.target.value }))} disabled={!editable} />
            <datalist id={uomListId}>{UOM_SUGGESTIONS.map(u => <option key={u} value={u} />)}</datalist>
          </div>
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#888', textAlign: 'right' }}>
          Extension: <span style={{ color: '#c4ccc1', fontFamily: 'var(--f-mono, var(--font-mono))' }}>${draftExtension.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <button
          style={{ fontSize: 11, padding: '5px 16px', borderRadius: 4, background: editable ? '#2e7d32' : '#555', color: '#fff', border: 'none', cursor: editable ? 'pointer' : 'default', fontWeight: 600 }}
          onClick={handleApply}
          disabled={!editable}
        >
          {isNewItem ? 'Add New Item' : `Apply to Line ${lineIndex + 1}`}
        </button>
        <button
          style={{ fontSize: 11, padding: '5px 16px', borderRadius: 4, border: '1px solid #5a7a52', background: 'transparent', color: '#c4ccc1', cursor: 'pointer' }}
          onClick={resetDraft}
        >Reset</button>
        {onNavigate && !isNewItem && (
          <>
            <button
              style={{ fontSize: 11, padding: '5px 16px', borderRadius: 4, border: '1px solid #5a7a52', background: 'transparent', color: '#c4ccc1', cursor: 'pointer' }}
              onClick={() => onNavigate('prev')}
            >{'◂'} Prev</button>
            <button
              style={{ fontSize: 11, padding: '5px 16px', borderRadius: 4, border: '1px solid #5a7a52', background: 'transparent', color: '#c4ccc1', cursor: 'pointer' }}
              onClick={() => onNavigate('next')}
            >Next {'▸'}</button>
          </>
        )}
      </div>
    </div>
  )
}
